import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Chip,
  IconButton,
  Avatar,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material'
import apiClient, { userService } from '../services/api'
import { Tenant } from '../types/api'
import toast from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'

// Dialog pentru creare/editare tenant
interface TenantDialogProps {
  open: boolean
  onClose: () => void
  tenant?: Tenant
  onSave: () => void
}

const TenantDialog: React.FC<TenantDialogProps> = ({ open, onClose, tenant, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    slug: ''
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (tenant) {
      setFormData({
        name: tenant.name,
        slug: tenant.slug
      })
    } else {
      setFormData({
        name: '',
        slug: ''
      })
    }
  }, [tenant, open])

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
  }

  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: tenant ? prev.slug : generateSlug(name)
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      toast.error('Numele tenant-ului este obligatoriu')
      return
    }

    try {
      setSaving(true)
      
      if (tenant) {
        // Update tenant existent
        await apiClient.put(`/tenants/${tenant.id}`, formData)
        toast.success('Tenant actualizat cu succes')
      } else {
        // Creare tenant nou
        await apiClient.post('/tenants', formData)
        toast.success('Tenant creat cu succes')
      }
      
      onSave()
      onClose()
    } catch (error: any) {
      console.error('Error saving tenant:', error)
      const errorMessage = error.response?.data?.detail || 'Eroare la salvarea tenant-ului'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const inputSx = { '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' }, '& .MuiFormHelperText-root': { fontSize: '0.68rem' } };
  const dialogPaperSx = {
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    '& .MuiInputLabel-root': { color: 'text.secondary', fontSize: '0.85rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
    '& .MuiOutlinedInput-root': {
      fontSize: '0.875rem',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
    },
    '& .MuiSelect-select': { color: 'text.primary' },
    '& .MuiMenuItem-root': { fontSize: '0.875rem' },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>{tenant ? 'Editare Tenant' : 'Tenant Nou'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField fullWidth label="Nume Tenant *" value={formData.name} onChange={(e) => handleNameChange(e.target.value)} required autoFocus size="small" sx={inputSx} />
            <TextField fullWidth label="Slug (URL) *" value={formData.slug} onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))} required helperText="Folosit pentru URL-uri și identificare unică" size="small" sx={inputSx} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={onClose} disabled={saving} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Anulează</Button>
          <Button type="submit" variant="contained" disabled={saving} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>{saving ? <CircularProgress size={16} /> : (tenant ? 'Actualizează' : 'Creează')}</Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

// Componenta principală pentru lista tenant-urilor
const TenantsListPage: React.FC = () => {
  const { user } = useAuth()
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false)
  const [selectedTenant, setSelectedTenant] = useState<Tenant | undefined>()

  // Încarcă tenant-urile
  const loadTenants = async () => {
    try {
      setLoading(true)
      const response = await userService.getAvailableTenants()
      setTenants(response)
    } catch (error: any) {
      console.error('Eroare la încărcarea tenant-urilor:', error)
      toast.error('Eroare la încărcarea tenant-urilor')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTenants()
  }, [])

  // Filtrează tenant-urile pe baza căutării
  const filteredTenants = tenants.filter(tenant =>
    tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.slug.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Paginare
  const paginatedTenants = filteredTenants.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleDeleteTenant = async (tenantId: string) => {
    if (!confirm('Sigur doriți să ștergeți acest tenant? Această acțiune nu poate fi anulată.')) {
      return
    }

    try {
      await apiClient.delete(`/tenants/${tenantId}`)
      toast.success('Tenant șters cu succes')
      loadTenants()
    } catch (error: any) {
      console.error('Error deleting tenant:', error)
      const errorMessage = error.response?.data?.detail || 'Eroare la ștergerea tenant-ului'
      toast.error(errorMessage)
    }
  }

  // Compact styles
  const inputSx = { '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } };
  const tableSx = { '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.78rem' }, '& .MuiTableBody-root .MuiIconButton-root': { p: 0.25 }, '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' } };

  return (
    <Box p={1.5}>
      {/* Header - Compact */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Gestionare Tenant-uri</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadTenants} disabled={loading} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>Actualizează</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelectedTenant(undefined); setTenantDialogOpen(true); }} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>Tenant Nou</Button>
        </Box>
      </Box>

      {/* Search - Compact */}
      <Paper sx={{ p: 1, mb: 1.5 }}>
        <TextField fullWidth label="Căutare" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} size="small" sx={inputSx} InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, fontSize: 18, color: 'text.secondary' }} /> }} placeholder="Caută după nume sau slug..." />
      </Paper>

      {/* Table - Compact */}
      <Paper sx={tableSx}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.900', '& th': { color: 'common.white', fontSize: '0.75rem', fontWeight: 600, py: 0.5, px: 1 } }}>
                <TableCell>Tenant</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Data Creării</TableCell>
                <TableCell>Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={20} /></TableCell></TableRow>
              ) : paginatedTenants.length === 0 ? (
                <TableRow><TableCell colSpan={4} align="center"><Typography sx={{ py: 2, fontSize: '0.8rem', color: 'text.secondary' }}>{searchTerm ? 'Nu s-au găsit tenant-uri.' : 'Nu s-au găsit tenant-uri.'}</Typography></TableCell></TableRow>
              ) : (
                paginatedTenants.map((tenant) => (
                  <TableRow key={tenant.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ mr: 1, bgcolor: 'primary.main', width: 24, height: 24 }}><BusinessIcon sx={{ fontSize: 14 }} /></Avatar>
                        <Box>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 500 }}>{tenant.name}</Typography>
                          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{tenant.id.slice(0, 8)}...</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell><Chip label={tenant.slug} size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.68rem' }} /></TableCell>
                    <TableCell><Typography sx={{ fontSize: '0.75rem' }}>{new Date(tenant.created_at).toLocaleDateString('ro-RO')}</Typography></TableCell>
                    <TableCell>
                      <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => { setSelectedTenant(tenant); setTenantDialogOpen(true); }}><EditIcon /></IconButton></Tooltip>
                      {user?.role === 'admin' && <Tooltip title="Șterge"><IconButton size="small" color="error" onClick={() => handleDeleteTenant(tenant.id)}><DeleteIcon /></IconButton></Tooltip>}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination rowsPerPageOptions={[10, 25, 50]} component="div" count={filteredTenants.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} labelRowsPerPage="Rânduri:" labelDisplayedRows={({ from, to, count }) => `${from}-${to} din ${count}`} sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' } }} />
      </Paper>

      {/* Dialog pentru creare/editare tenant */}
      <TenantDialog
        open={tenantDialogOpen}
        onClose={() => {
          setTenantDialogOpen(false)
          setSelectedTenant(undefined)
        }}
        tenant={selectedTenant}
        onSave={loadTenants}
      />
    </Box>
  )
}

export default TenantsListPage
